import { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Link, useHistory, useParams } from 'react-router-dom'
import { Spin, Typography } from 'antd'
import { TeamOutlined, UserOutlined } from '@ant-design/icons'
import { useApi } from '../../api/useApi'
import SingleObject from '../../components/singleObject'
import StatusMessage from '../../components/statusMessage/StatusMessage'
import { setFlags } from '../../api/flagApi'
import { IPerson, hämtaMedarbetare, sökMedarbetarePåNamn } from '../../api/medarbetareApi'

/**
 * Personalsidan gäller alltid en och samma person och adresseras på Pejlens id (#99).
 * Två personer med samma namn har var sin sida, och den som byter namn behåller sin.
 *
 * Gamla länkar och bokmärken har namnet i adressen. De skickas vidare till personens sida,
 * eller till ett val när flera personer har namnet.
 */
const SingleMedarbetare = () => {
  const { id } = useParams<{ id: string }>()
  return /^\d+$/.test(id) ? <Personalsida id={Number(id)} /> : <GammalNamnlänk namn={decodeURIComponent(id)} />
}

// useApi skapar en ny instans vid varje rendering. Sidan har eget tillstånd (namnet) och ritas om,
// så instansen hålls fast: annars körs hämtningarna om i en oändlig loop
const useFastApi = () => useRef(useApi()).current

const Personalsida = ({ id }: { id: number }) => {
  const dispatch = useDispatch()
  const api = useFastApi()
  const [person, setPerson] = useState<IPerson | null | undefined>(undefined)

  // Bruten ur useEffect så den kan köras om efter en sparad hantering — se brukare/single.
  const laddaFlaggor = useCallback(() => {
    setFlags(api, dispatch, 'medarbetare', String(id))
  }, [api, dispatch, id])

  useEffect(() => {
    laddaFlaggor()
  }, [laddaFlaggor])

  useEffect(() => {
    let aktuell = true
    setPerson(undefined)
    hämtaMedarbetare(api, id).then((p) => {
      if (aktuell) setPerson(p ?? null)
    })
    return () => {
      aktuell = false
    }
  }, [api, id])

  if (person === null) return <StatusMessage icon={<UserOutlined />} title="Personen finns inte i Pejlen" />

  return <SingleObject obj={{ namn: person?.namn ?? '', id }} heading={'Personal'} onHandled={laddaFlaggor} />
}

const GammalNamnlänk = ({ namn }: { namn: string }) => {
  const api = useFastApi()
  const history = useHistory()
  const [träffar, setTräffar] = useState<IPerson[] | undefined>(undefined)

  useEffect(() => {
    let aktuell = true
    sökMedarbetarePåNamn(api, namn).then((personer) => {
      if (!aktuell) return
      // En träff: byt adressen, så att bakåtknappen inte leder tillbaka hit
      if (personer.length === 1) history.replace(`/app/medarbetare/${personer[0].id}`)
      else setTräffar(personer)
    })
    return () => {
      aktuell = false
    }
  }, [api, history, namn])

  if (träffar === undefined) return <Spin style={{ display: 'block', margin: '96px auto' }} />
  if (träffar.length === 0) return <StatusMessage icon={<UserOutlined />} title="Personen finns inte i Pejlen" />

  return (
    <div style={{ maxWidth: 420, margin: '0 auto', padding: '96px 24px 24px', textAlign: 'center' }}>
      <span style={{ fontSize: 32, color: 'rgba(0, 0, 0, 0.25)', lineHeight: 1 }}>
        <TeamOutlined />
      </span>
      <Typography.Title level={4}>Flera personer heter {namn}</Typography.Title>
      <Typography.Text type="secondary">Välj vilken person du vill se.</Typography.Text>
      <ul style={{ listStyle: 'none', padding: 0, marginTop: 16 }}>
        {träffar.map((p, i) => (
          <li key={p.id} style={{ marginBottom: 8 }}>
            <Link to={`/app/medarbetare/${p.id}`}>
              {p.namn} ({i + 1})
            </Link>
          </li>
        ))}
      </ul>
    </div>
  )
}

export default SingleMedarbetare
